import { useCallback, useEffect, useRef, useState } from 'react';

import { sendStatistics } from '../api/session';
import {
  redirectSessionTerminal,
  type SeastarReason,
} from '../utils/sessionRedirect';
import { useReconnectStateMachine } from './useReconnectStateMachine';

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1000;

interface UseWebSocketTransportProps {
  sessionId: string;
  activeWindowId: string;
  seastarLocation?: string;
  onBinaryMessage: (
    data: Uint8Array,
    timestamp: number,
  ) => Promise<void> | void;
  onJsonMessages: (messages: unknown[]) => void;
  onBeforeReconnect?: () => void;
}

export const useWebSocketTransport = ({
  sessionId,
  activeWindowId,
  seastarLocation,
  onBinaryMessage,
  onJsonMessages,
  onBeforeReconnect,
}: UseWebSocketTransportProps) => {
  const [isConnected, setIsConnected] = useState(false);

  const isCleanedUpRef = useRef(false);
  const terminalHandledRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const messageQueueRef = useRef<MessageEvent[]>([]);
  const isProcessingQueueRef = useRef(false);

  // Keep dynamic references to consumer callbacks so we don't rebuild the
  // WebSocket connection when callback identities change.
  const onBinaryMessageRef = useRef(onBinaryMessage);
  const onJsonMessagesRef = useRef(onJsonMessages);
  const onBeforeReconnectRef = useRef(onBeforeReconnect);

  useEffect(() => {
    onBinaryMessageRef.current = onBinaryMessage;
    onJsonMessagesRef.current = onJsonMessages;
    onBeforeReconnectRef.current = onBeforeReconnect;
  }, [onBinaryMessage, onJsonMessages, onBeforeReconnect]);

  // Mirror refs for in-component callbacks. They are written synchronously
  // below after their declaration, eliminating any use-before-define issues
  // between the reconnect state machine and the rest of the hook.
  const connectWebSocketRef = useRef<() => void>(() => {});
  const handleStreamTerminatedRef = useRef<
    (reason: SeastarReason) => void | Promise<void>
  >(() => {});

  const reconnectStateMachine = useReconnectStateMachine({
    maxAttempts: MAX_RECONNECT_ATTEMPTS,
    baseDelayMs: BASE_RECONNECT_DELAY_MS,
    onReconnect: () => {
      if (!isCleanedUpRef.current) {
        if (onBeforeReconnectRef.current) {
          onBeforeReconnectRef.current();
        }
        connectWebSocketRef.current();
      }
    },
    onExhausted: () => {
      console.error(
        `Failed to connect after ${MAX_RECONNECT_ATTEMPTS + 1} attempts`,
      );
      handleStreamTerminatedRef.current('SessionError');
    },
  });

  const {
    reset: resetAttempts,
    schedule: scheduleReconnect,
    clear: clearReconnect,
    getAttempts,
  } = reconnectStateMachine;

  const processQueue = useCallback(async () => {
    if (isProcessingQueueRef.current) return;
    isProcessingQueueRef.current = true;

    try {
      while (messageQueueRef.current.length > 0) {
        const event = messageQueueRef.current.shift();
        if (!event) continue;

        if (typeof event.data === 'string') {
          try {
            const messages = JSON.parse(event.data) as unknown;
            if (Array.isArray(messages)) {
              onJsonMessagesRef.current(messages);
            }
          } catch (e) {
            console.error('Error parsing JSON message:', e);
          }
        } else {
          const data = new Uint8Array(event.data);
          await onBinaryMessageRef.current(data, event.timeStamp * 1000);
        }
      }
    } finally {
      isProcessingQueueRef.current = false;
    }
  }, []);

  const handleStreamTerminated = useCallback(
    async (reason: SeastarReason): Promise<void> => {
      if (isCleanedUpRef.current || terminalHandledRef.current) return;
      terminalHandledRef.current = true;
      isCleanedUpRef.current = true;

      clearReconnect();

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      await sendStatistics(sessionId, {});
      redirectSessionTerminal(seastarLocation, reason);
    },
    [sessionId, seastarLocation, clearReconnect],
  );
  handleStreamTerminatedRef.current = handleStreamTerminated;

  const connectWebSocket = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/bi/v1/sessions/${sessionId}/websocket/${activeWindowId}`;

    console.log(
      `Connecting to WebSocket (attempt ${getAttempts() + 1}/${MAX_RECONNECT_ATTEMPTS + 1}):`,
      wsUrl,
    );

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      resetAttempts(); // Reset attempts on successful connection
      setIsConnected(true);
    };

    ws.onclose = (event) => {
      setIsConnected(false);

      if (isCleanedUpRef.current || terminalHandledRef.current) return;

      if (event.wasClean) {
        handleStreamTerminated('SessionEnd');
      } else {
        scheduleReconnect();
      }
    };

    ws.onerror = (event) => {
      console.error('WebSocket error:', event);
    };

    ws.onmessage = (event) => {
      messageQueueRef.current.push(event);
      void processQueue();
    };
  }, [
    sessionId,
    activeWindowId,
    getAttempts,
    resetAttempts,
    scheduleReconnect,
    handleStreamTerminated,
    processQueue,
  ]);
  connectWebSocketRef.current = connectWebSocket;

  useEffect(() => {
    isCleanedUpRef.current = false;
    terminalHandledRef.current = false;
    resetAttempts();

    connectWebSocket();

    return () => {
      isCleanedUpRef.current = true;
      clearReconnect();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      messageQueueRef.current = [];
      isProcessingQueueRef.current = false;
    };
  }, [connectWebSocket, resetAttempts, clearReconnect]);

  return { isConnected };
};
